/* eslint-disable thsjs/expression-complexity */
/* eslint-disable thsjs/elseif-without-else */
/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  type CustomCircleStyle,
  type CustomLineStyle,
  type CustomRectStyle,
  type CustomSeriesRenderItemAPI,
  type CustomTextStyle,
  type CustomSeriesRenderItemParams
} from 'echarts';
import { util, graphic } from 'echarts/core';
import type { DvMarkerOption, Option } from '../../../../core/option';
import { isArray, normalizeToArray } from '../../../../util/utils';
import { BoundingRect } from '../../../util/coord';
import type { DvMarkerData, markerRenderItemReturn } from '../type';

// curvedPointer图形专用style配置 要改成echarts的配置项
export interface CurvedPointerStyleProps {
  lineStyle: CustomLineStyle & {
    style: { lineHeight: number; dvClockwise: boolean; dvWidth: number; dvHeight: number };
  };
  circleStyle: CustomCircleStyle;
  rectStyle: CustomRectStyle;
  textStyle: CustomTextStyle;
  // TODO arrowStyle
}

/**
 * 多柱状图下需要对起点的x坐标进行偏移
 */
export function prepareCurvedPointerPoint(
  api: CustomSeriesRenderItemAPI,
  data: DvMarkerData,
  option: Option
) {
  const barSeries = normalizeToArray(option?.series).filter(series => series.type === 'bar');
  if (barSeries.length <= 1) {
    return;
  }
  const currentSeriesIndices = api.currentSeriesIndices();
  const seriesIndex = data.__seriesIndex || 0;
  const barLayout = api.barLayout({
    barGap: option?.series[seriesIndex]?.barGap || '30%',
    barCategoryGap: option?.series[seriesIndex]?.barCategoryGap || '20%',
    count: currentSeriesIndices.length - 1
  });
  const point = api.coord([api.value(0), api.value(1)]);
  point[0] += barLayout[seriesIndex].offsetCenter;
  api.__point = point;
}

function getEndPoint(
  point: [number, number],
  grid: BoundingRect,
  dvWidth: number,
  dvHeight: number
) {
  // 解构输入
  const offsetX = dvWidth;
  const offsetY = dvHeight;
  let endPointX = point[0];
  let endPointY = point[1];
  let [px, py] = point;
  const { x: gx, y: gy, width: gw, height: gh } = grid;

  // 计算网格的中心点和四等分点
  const centerX = gx + gw / 2;
  const centerY = gy + gh / 2;
  const quarterX = gw / 4;
  const quarterY = gh / 4;

  // 为保证是曲线，将这三种特殊情况下的点进行略微偏移
  if (px === centerX && py === centerY) {
    // 点在正中心
    px -= Math.random() < 0.5 ? 0.5 : -0.5;
    py -= Math.random() < 0.5 ? 0.5 : -0.5;
  }
  if (px === centerX) {
    // 点在垂直中线上
    px -= Math.random() < 0.5 ? 0.5 : -0.5;
  }
  if (py === centerY) {
    // 点在水平中线上
    py -= Math.random() < 0.5 ? 0.5 : -0.5;
  }

  // 初始化方向
  let direction = '';

  // 确定水平方向
  if (px < centerX - quarterX) {
    direction += 'r';
    endPointX += offsetX;
  } else if (px > centerX + quarterX) {
    direction += 'l';
    endPointX -= offsetX;
  } else if (px < centerX) {
    direction += 'l';
    endPointX -= offsetX;
  } else if (px > centerX) {
    direction += 'r';
    endPointX += offsetX;
  }

  // 确定垂直方向
  if (py < centerY - quarterY) {
    direction += 'b';
    endPointY += offsetY;
  } else if (py > centerY + quarterY) {
    direction += 't';
    endPointY -= offsetY;
  } else if (py < centerY) {
    direction += 't';
    endPointY -= offsetY;
  } else if (py > centerY) {
    direction += 'b';
    endPointY += offsetY;
  }

  // 修正中心点附近的方向
  if (Math.abs(px - centerX) <= quarterX && Math.abs(py - centerY) <= quarterY) {
    if (direction.includes('r')) {
      direction = direction.replace('r', 'l');
      endPointX -= offsetX * 2;
    } else if (direction.includes('l')) {
      direction = direction.replace('l', 'r');
      endPointX += offsetX * 2;
    }
    if (direction.includes('b')) {
      direction = direction.replace('b', 't');
      endPointY -= offsetY * 2;
    } else if (direction.includes('t')) {
      direction = direction.replace('t', 'b');
      endPointY += offsetY * 2;
    }
  }

  return {
    direction,
    endPoint: [endPointX, endPointY]
  };
}

/**
 * 文本换行
 * @param str
 * @param num
 * @returns
 */
function insertNewline(str, num) {
  if (num <= 0) {
    return str;
  }

  // dvBoxLayout可能引起二次绘图，导致原本文本中存在'\n'
  const newStr = str.split('\n').join('');
  let result = '';
  for (let i = 0; i < newStr.length; i++) {
    if (i > 0 && i % num === 0) {
      result += '\n';
    }
    result += newStr[i];
  }

  return result;
}

function getTextRect(textStyle: CustomTextStyle) {
  const content = new graphic.Text(textStyle);
  const { x, y, width, height } = content.getBoundingRect();
  const padding = content.style.padding || [0, 0, 0, 0];
  return {
    x,
    y,
    // 满足富文本内边距设置
    width: width + padding[1] + padding[3],
    height: height + padding[0] + padding[2]
  };
}

const handleRedius = (
  lineHeight: number,
  rectWidth: number,
  radius: unknown
): [unknown, unknown, unknown, unknown] | unknown => {
  const r = radius || 2;
  return [r, r, r, r];
};

/**
 * @implements
 * 1. 水平方向： 左侧能放下就放左侧 放不下放右侧
 * 2. 竖直方向：
 *  2.1 中轴向上，默认放下面，下面放不下才放上面
 *  2.2 中轴向下，默认放上面，上面放不下才放下面
 * @param lHeight
 * @param rWidth
 * @param point
 * @param width
 * @param height
 * @returns
 */
const handleFlagPosition = (
  lHeight: number,
  rWidth: number,
  point: [number, number],
  gridRect: BoundingRect,
  dvWidth: number,
  dvHeight: number
) => {
  const { endPoint, direction } = getEndPoint(point, gridRect, dvWidth, dvHeight);
  let labelOffsetX;
  let labelOffsetY;
  let dvClockwise;

  if (direction === 'lb') {
    dvClockwise = true;
    labelOffsetX = -rWidth;
    labelOffsetY = -lHeight / 2;
  }
  if (direction === 'lt') {
    dvClockwise = false;
    labelOffsetX = -rWidth;
    labelOffsetY = -lHeight / 2;
  }
  if (direction === 'rb') {
    dvClockwise = false;
    labelOffsetX = 0;
    labelOffsetY = -lHeight / 2;
  }
  if (direction === 'rt') {
    dvClockwise = true;
    labelOffsetX = 0;
    labelOffsetY = -lHeight / 2;
  }

  return {
    endPoint,
    labelOffsetX,
    labelOffsetY,
    dvClockwise
  };
};

// eslint-disable-next-line complexity
export function curvedPointer(
  data: DvMarkerData,
  seriesOption: DvMarkerOption,
  api: CustomSeriesRenderItemAPI,
  params: CustomSeriesRenderItemParams,
  dataIndex: number
): markerRenderItemReturn {
  const { itemStyle } = data;
  let dvAnimationConfig = util.clone(seriesOption.dvAnimationConfig || {});
  const { circleStyle, lineStyle, rectStyle, textStyle } = itemStyle;
  textStyle.style.text = insertNewline(textStyle.style.text, 6);
  const { lineHeight: lHeight, dvWidth = 50, dvHeight = 50, dvShowArrow = false } = lineStyle.style;
  const coords = [api.value(0), api.value(1)];
  const point = api?.__point || (api.coord([coords[0], coords[1]]) as [number, number]);
  // !!!!!!!!   todo 缺少临界判断
  const { r: radius = 0 } = rectStyle?.shape || {};
  let rWidth = rectStyle.shape.width;
  let rectHeight = rectStyle.shape.height;
  const { text: textContent, x: textOffsetX, y: textOffsetY } = textStyle.style;

  const gridRect = params.coordSys;
  // 自适应文本宽高 通过预渲染text获取包围矩形宽高
  if (typeof rWidth === 'undefined' || typeof rectHeight === 'undefined') {
    const { width: tWidth, height: tHeight } = getTextRect(textStyle);
    rWidth = tWidth;
    rectHeight = tHeight;
  }
  // 调整标签的朝向
  const { endPoint, labelOffsetX, labelOffsetY, dvClockwise } = handleFlagPosition(
    rectHeight,
    rWidth,
    point,
    gridRect,
    dvWidth,
    dvHeight
  );
  // 调整标签的动画参数 主要是为流水线显示设置延迟时间
  if (
    dvAnimationConfig.dvStreamAnimation &&
    typeof dvAnimationConfig?.enterAnimation?.delay === 'undefined'
  ) {
    const categoryIndex = coords[0];
    const { duration } = dvAnimationConfig.enterAnimation;
    const streamDelay = duration * categoryIndex;
    dvAnimationConfig.enterAnimation.delay = streamDelay;
    const { dvStreamAnimation, ...othersConfig } = dvAnimationConfig;
    dvAnimationConfig = othersConfig;
  }
  const rectRadius = isArray(radius) ? radius : handleRedius(lHeight, rWidth, radius);
  const { shape, ...elRectStyle } = rectStyle;
  const { style: { x, y, ...otherTextStyle } = {}, ...elTextStyle } = textStyle;
  elTextStyle.style = otherTextStyle;

  const circle = {
    type: 'circle',
    extra: {
      dvid: `curvedPointer_circle_${dataIndex}`
    },
    x: point[0],
    y: point[1],
    ...dvAnimationConfig
  };
  const line = {
    type: 'curveLine',
    extra: {
      dvid: `curvedPointer_line_${dataIndex}`
    },
    shape: {
      sx: point[0],
      sy: point[1],
      ex: endPoint[0],
      ey: endPoint[1],
      clockwise: dvClockwise,
      showArrow: dvShowArrow
    },
    ...dvAnimationConfig
  };
  const rect = {
    type: 'rect',
    extra: {
      dvid: `curvedPointer_rect_${dataIndex}`
    },
    shape: {
      x: labelOffsetX,
      y: labelOffsetY,
      width: rWidth,
      r: rectRadius,
      height: rectHeight
    },
    ...dvAnimationConfig
  };
  const text = {
    type: 'text',
    extra: {
      dvid: `curvedPointer_text_${dataIndex}`
    },
    x: labelOffsetX,
    y: labelOffsetY,
    style: {
      x: rWidth > 0 ? textOffsetX : rWidth + textOffsetX,
      // 消除text计算包围盒多余的高度 满足padding
      // 具体原因待查明
      y: textOffsetY || 0,
      text: textContent
    },
    ...dvAnimationConfig
  };
  util.merge(circle, circleStyle);
  util.merge(line, lineStyle);
  util.merge(rect, elRectStyle);
  util.merge(text, elTextStyle, true);

  return {
    $mergeChildren: false,
    type: 'group',
    extra: {
      dvid: `curvedPointer_group_outside_${dataIndex}`
    },
    children: [
      circle,
      {
        type: 'group',
        extra: {
          dvid: `curvedPointer_group_middle_${dataIndex}`
        },
        children: [
          line,
          {
            type: 'group',
            extra: {
              dvid: `curvedPointer_group_inside_${dataIndex}`
            },
            x: endPoint[0],
            y: endPoint[1],
            children: [rect, text]
          }
        ]
      }
    ],
    silent: true
  };
}
